import { duckDbIdentifier } from '@cotera/client/app/etc/duckdb';
import { isInternalDatasetColumn } from '@cotera/sdk/core';
import { DATASET_CUSTOM_QUERY_TABLE } from './source-sql';

/**
 * Completions for the preview footer's ad-hoc DuckDB box.
 *
 * Deliberately lexical: it looks at the word the caret sits in and nothing
 * else. The box is a one-line input rather than an editor, and what it is
 * mostly used for is finishing a column name that is already on screen — a
 * parse would buy clause awareness nobody is asking this input for.
 *
 * The two places it does look further than the word are the ones where
 * completing blind would produce broken SQL: inside a string literal (where a
 * column name is just text) and after a `.` (where only a column can follow).
 */

export type SqlCompletionKind = 'column' | 'table' | 'keyword';

export type SqlCompletionColumn = {
  name: string;
  type?: string | null;
};

export type SqlCompletion = {
  /** Shown in the menu: the bare name, never quoted. */
  label: string;
  /** What replaces the token — quoted when DuckDB would not take it bare. */
  insert: string;
  kind: SqlCompletionKind;
  /** A column's SQL type, shown next to the label. */
  detail?: string;
};

export type SqlCompletionContext = {
  /** The partial word before the caret; empty after a `.` or an opening `"`. */
  token: string;
  /** Half-open range of the statement an accepted completion replaces. */
  start: number;
  end: number;
  options: SqlCompletion[];
};

/** As many as the menu will show; a wide dataset otherwise buries the point. */
const MAX_COMPLETIONS = 12;

/**
 * The keywords and functions worth finishing here. Not DuckDB's full grammar:
 * the box only accepts SELECT/WITH statements, so DDL and DML are noise, and a
 * keyword the user has to scroll past is worse than one they type out.
 */
const SQL_KEYWORDS: readonly string[] = [
  'SELECT',
  'FROM',
  'WHERE',
  'GROUP BY',
  'ORDER BY',
  'HAVING',
  'QUALIFY',
  'LIMIT',
  'OFFSET',
  'DISTINCT',
  'WITH',
  'AS',
  'ASC',
  'DESC',
  'AND',
  'OR',
  'NOT',
  'NULL',
  'IS NULL',
  'IS NOT NULL',
  'IN',
  'LIKE',
  'ILIKE',
  'BETWEEN',
  'CASE',
  'WHEN',
  'THEN',
  'ELSE',
  'END',
  'JOIN',
  'LEFT JOIN',
  'INNER JOIN',
  'ON',
  'USING',
  'UNION ALL',
  'EXCLUDE',
  'COUNT',
  'SUM',
  'AVG',
  'MIN',
  'MAX',
  'ROUND',
  'ABS',
  'COALESCE',
  'CAST',
  'TRY_CAST',
  'LENGTH',
  'LOWER',
  'UPPER',
  'TRIM',
  'SUBSTRING',
  'REPLACE',
  'CONCAT',
  'REGEXP_MATCHES',
  'DATE_TRUNC',
  'EXTRACT',
  'STRFTIME',
  'NOW',
];

/**
 * Words DuckDB will not read as an identifier unquoted. Only the ones a column
 * plausibly gets named — the point is to decide whether to quote, not to
 * mirror the parser.
 */
const RESERVED_WORDS: ReadonlySet<string> = new Set([
  'ALL',
  'AND',
  'AS',
  'ASC',
  'BETWEEN',
  'BY',
  'CASE',
  'CAST',
  'CHECK',
  'COLLATE',
  'COLUMN',
  'CREATE',
  'DEFAULT',
  'DESC',
  'DISTINCT',
  'ELSE',
  'END',
  'EXCEPT',
  'FALSE',
  'FROM',
  'GROUP',
  'HAVING',
  'IN',
  'INTERSECT',
  'INTO',
  'IS',
  'JOIN',
  'LIKE',
  'LIMIT',
  'NOT',
  'NULL',
  'OFFSET',
  'ON',
  'OR',
  'ORDER',
  'PRIMARY',
  'REFERENCES',
  'SELECT',
  'TABLE',
  'THEN',
  'TO',
  'TRUE',
  'UNION',
  'UNIQUE',
  'USING',
  'WHEN',
  'WHERE',
  'WITH',
]);

const BARE_IDENTIFIER = /^[A-Za-z_][A-Za-z0-9_$]*$/;

const isWordChar = (char: string): boolean => /[A-Za-z0-9_$]/.test(char);

/** The name as it has to be written to survive a round trip through DuckDB. */
const identifierInsert = (name: string): string =>
  BARE_IDENTIFIER.test(name) && !RESERVED_WORDS.has(name.toUpperCase())
    ? name
    : duckDbIdentifier(name);

/**
 * True when the caret is somewhere a name is not a name: inside a `'...'`
 * literal or past a `--` comment. Quote parity is enough for both — `''` is an
 * escaped quote and leaves parity untouched, which is the answer we want.
 */
const isInert = (head: string): boolean => {
  const line = head.slice(head.lastIndexOf('\n') + 1);
  if (line.includes('--')) {
    return true;
  }
  let quotes = 0;
  for (const char of head) {
    if (char === `'`) {
      quotes += 1;
    }
  }
  return quotes % 2 === 1;
};

/**
 * How well a candidate answers the token: 0 for a prefix, 1 for a name that
 * merely contains it, null for no.
 *
 * Only names get the substring pass. A column called `customer_email` is worth
 * finding by typing `email`, whereas a keyword matched that loosely buries the
 * columns under a dozen keywords that happen to share a letter.
 */
const matchRank = (candidate: SqlCompletion, token: string): number | null => {
  if (token === '') {
    return 0;
  }
  const haystack = candidate.label.toLowerCase();
  const needle = token.toLowerCase();
  if (haystack.startsWith(needle)) {
    return 0;
  }
  if (candidate.kind === 'keyword') {
    return null;
  }
  return haystack.includes(needle) ? 1 : null;
};

const KIND_ORDER: Record<SqlCompletionKind, number> = {
  column: 0,
  table: 1,
  keyword: 2,
};

/**
 * What the caret is in the middle of typing, and what could finish it.
 *
 * Returns null when there is nothing to offer: an empty word (the menu would
 * be the whole schema on every space), a string literal, or a token nothing
 * matches.
 */
export const sqlCompletionsAt = ({
  value,
  caret,
  columns,
  tableName = DATASET_CUSTOM_QUERY_TABLE,
}: {
  value: string;
  caret: number;
  columns: readonly SqlCompletionColumn[];
  tableName?: string;
}): SqlCompletionContext | null => {
  const position = Math.max(0, Math.min(caret, value.length));
  const head = value.slice(0, position);
  if (isInert(head)) {
    return null;
  }

  let start = position;
  while (start > 0 && isWordChar(value.charAt(start - 1))) {
    start -= 1;
  }
  const token = value.slice(start, position);

  // A word the caret sits inside is replaced whole, so completing the middle
  // of `reven|ue` gives `revenue` rather than `revenuenue`.
  let end = position;
  while (end < value.length && isWordChar(value.charAt(end))) {
    end += 1;
  }

  // An opening `"` means the user has already committed to a quoted name, so
  // the quote is part of what gets replaced and the closing one — if they
  // typed it — goes with it.
  const quoted =
    start > 0 &&
    value.charAt(start - 1) === '"' &&
    (value.slice(0, start - 1).split('"').length - 1) % 2 === 0;
  if (quoted) {
    start -= 1;
    if (value.charAt(end) === '"') {
      end += 1;
    }
  }

  // `dataset.` can only be followed by one of its columns.
  const qualified = start > 0 && value.charAt(start - 1) === '.';

  if (token === '' && !quoted && !qualified) {
    return null;
  }

  const columnOptions: SqlCompletion[] = columns
    .filter((column) => !isInternalDatasetColumn(column.name))
    .map((column) => ({
      label: column.name,
      insert: quoted
        ? duckDbIdentifier(column.name)
        : identifierInsert(column.name),
      kind: 'column' as const,
      ...(column.type !== null &&
        column.type !== undefined && { detail: column.type }),
    }));

  const candidates: SqlCompletion[] = qualified
    ? columnOptions
    : quoted
    ? [
        ...columnOptions,
        {
          label: tableName,
          insert: duckDbIdentifier(tableName),
          kind: 'table',
        },
      ]
    : [
        ...columnOptions,
        {
          label: tableName,
          insert: identifierInsert(tableName),
          kind: 'table',
        },
        ...SQL_KEYWORDS.map((keyword) => ({
          label: keyword,
          // Trailing space: a keyword is never the end of the statement, and
          // it also closes the menu, which an exact-match token would not.
          insert: `${keyword} `,
          kind: 'keyword' as const,
        })),
      ];

  const options = candidates
    .flatMap((candidate) => {
      const rank = matchRank(candidate, token);
      return rank === null ? [] : [{ candidate, rank }];
    })
    // Kind before rank: this box exists to query the dataset, so a column the
    // token merely appears inside still beats a keyword that starts with it.
    .sort(
      (left, right) =>
        KIND_ORDER[left.candidate.kind] - KIND_ORDER[right.candidate.kind] ||
        left.rank - right.rank ||
        left.candidate.label.localeCompare(right.candidate.label)
    )
    .slice(0, MAX_COMPLETIONS)
    .map(({ candidate }) => candidate);

  return options.length === 0 ? null : { token, start, end, options };
};

/** The statement with the completion written in, and where the caret lands. */
export const applySqlCompletion = ({
  value,
  start,
  end,
  insert,
}: {
  value: string;
  start: number;
  end: number;
  insert: string;
}): { value: string; caret: number } => ({
  value: `${value.slice(0, start)}${insert}${value.slice(end)}`,
  caret: start + insert.length,
});
